'use client';

/* ──────────────────────────────────────────────
 *  Booking Tunnel V2 — React Context (XState)
 * ────────────────────────────────────────────── */

import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';

import type {
  BookingCallbacks,
  BookingContext as BookingMachineContext,
  BookingFilterConfig,
  BookingFunnelEvent,
  BookingLabels,
  BookingParams,
  BookingPrestation,
  BookingStep,
  BookingStepChangeInfo,
} from '../types';
import { bookingLabelsFor } from '../types';
import { DEFAULT_FLAG_BASE_URL } from '../components/shared/CountryFlag';
import { type BookingEvent, bookingMachine, stepFromState, visibleSteps } from './booking-machine';
import { useBookingFunnel } from './use-booking-funnel';
import {
  clearBookingCart,
  clearBookingCartRestorable,
  isBookingCartFromThisVisit,
  peekBookingCartIncoming,
  readBookingCart,
  saveBookingCartLines,
  setBookingCartRestorable,
  takeBookingCartIncoming,
} from '../cart/booking-cart-store';
import { toBookingCartLine } from '../cart/booking-cart-lines';
import type { BookingCartLine } from '../cart/booking-cart-store';
import {
  expressCatalogueTarget,
  resolveCartEntry,
  resumeRestorableSelection,
  type RestorableSelection,
} from '../cart/resolve-cart-entry';
import { isValidISODate, resolveInitialWindowStart } from '../utils/date-helpers';
import { clearCheckoutDraft } from '../utils/customer-form';
import { REDIRECT_RETURN_PARAM } from '../../pro-payment/redirect-return';
import {
  redirectReturnMessage,
  useRedirectReturn,
  type RedirectFinalOutcome,
  type RedirectReturnPhase,
} from '../../pro-payment/useRedirectReturn';

const noop = (): void => {
  /* intentional no-op */
};

/**
 * Attente maximale de la libération d'une pré-réservation (le DELETE annule d'abord l'intent Stripe) avant que le
 * résumé du panier reparte quand même — quitte à voir retiré un code que le serveur tient encore.
 */
export const PRE_RESERVATION_RELEASE_WAIT_MS = 10_000;

/** DELETE d'une pré-réservation en vol : la requête de l'hôte, et sa fin (ou le délai borné) pour qui l'attend. */
interface PreReservationRelease {
  uuid: string;
  request: Promise<void>;
  settled: Promise<void>;
}

/**
 * Sorties de la finalisation qui libèrent la pré-réservation tout de suite (spec Parcours § 7.6) : retour, lien
 * « modifier » du récap, coordonnées à modifier, minuteur expiré. Le créneau, la carte cadeau et le code promo
 * qu'elle tient sont rendus sans attendre le cron d'expiration. `checkoutBusy` : création, confirmation ou paiement
 * en cours — un minuteur qui expire pendant un `confirmPayment` (3-D Secure, départ vers Klarna) ne supprime jamais
 * la pré-réservation que Stripe est peut-être en train de régler ; le cron la rendra si le paiement n'aboutit pas.
 */
function leavesPreReservation(event: BookingEvent, checkoutBusy: boolean): boolean {
  switch (event.type) {
    case 'BACK':
    case 'EDIT_STEP':
    case 'PRE_RESERVATION_RELEASED':
      return true;
    case 'TIMER_EXPIRED':
      return !checkoutBusy;
    default:
      return false;
  }
}

/**
 * Whether the tunnel has moved past a returned Stripe payment (see useRedirectReturn):
 * - final machine state: another path already ended the tunnel — an inline payment reached the
 *   success screen, or the catalogue failed to load (error screen, the payment outcome unknown);
 * - another pre-reservation: the customer started over, a late confirmation of the returned payment
 *   must not replace it (its form would vanish, possibly after being paid).
 */
export function bookingRedirectOutcome(
  snapshot: { status: string; value: unknown; context: { preReservationUuid?: string } },
  returnedUuid: string,
): RedirectFinalOutcome | undefined {
  if (snapshot.status === 'done') return snapshot.value === 'success' ? 'success' : 'error';
  // L'écran d'erreur n'est plus un état final (« Réessayer ») : il clôt tout de même la vérification.
  if (snapshot.value === 'error') return 'error';
  const current = snapshot.context.preReservationUuid;
  return current && current !== returnedUuid ? 'superseded' : undefined;
}

// ── Context value ───────────────────────────────

/**
 * Sélection retrouvée d'une visite précédente. `resume` : rien n'est sélectionné (« Reprendre / Recommencer ») ;
 * `add` : le client arrive avec une nouvelle prestation (« Ajouter à votre sélection précédente / Remplacer »).
 */
export interface RestoredSelectionNotice {
  names: string[];
  /** Sélection en cours (`add`) : la prestation qui vient d'arriver. */
  currentNames: string[];
  savedAt: number;
  kind: 'resume' | 'add';
}

/** Bandeau du retour de redirection Stripe. `unverified` = budget de vérification épuisé sans preuve de paiement. */
interface RedirectNotice {
  type: RedirectReturnPhase;
  message: string;
}

interface BookingContextValue {
  /** Current XState context (data) */
  ctx: BookingMachineContext;
  /** Current step derived from machine state */
  currentStep: BookingStep | 'loading' | 'error';
  /** Send event to the machine */
  send: (event: BookingEvent) => void;
  /**
   * Callbacks for API calls. `onCancelPreReservation` y est suivi par le contexte : un seul DELETE en vol par
   * pré-réservation (les appels concurrents partagent sa promesse) et `whenPreReservationReleased` en attend la fin.
   */
  callbacks: BookingCallbacks;
  /** UI labels */
  labels: BookingLabels;
  /** Pro booking params */
  params: BookingParams;
  /** Locale */
  locale: string;
  /** Currency */
  currency: string;
  /** Stripe publishable key (paid bookings) */
  stripePublishableKey: string | undefined;
  /** Professional name */
  professionalName: string;
  /** Professional public slug */
  professionalSlug: string;
  /** Code carte cadeau pré-rempli dans le champ « carte cadeau » (ex. depuis le QR `?code=…`). */
  prefilledVoucherCode?: string;
  /** Code promo pré-rempli dans le champ « code promo » (ex. depuis une campagne `?promo=…`). */
  prefilledPromoCode?: string;
  /** Redirect automatically to the public manage page after confirmation */
  redirectToManageOnSuccess: boolean;
  /** Build the public manage URL for a confirmed reservation */
  getManageBookingUrl?: (params: { professionalSlug: string; reservationUuid: string }) => string | undefined;
  /** Success screen behavior after the reservation is confirmed */
  successMode: 'message' | 'manage-inline';
  /** Format cents → price string */
  formatPrice: (cents: number) => string;
  /** Navigate back */
  goBack: () => void;
  /** Nouvel essai de chargement du catalogue après une erreur (bouton « Réessayer »). */
  retryLoad: () => void;
  /** Libère la pré-réservation en cours (« Retour au site », coordonnées à modifier). */
  releasePreReservation: () => void;
  /**
   * Fin de la libération en cours de la pré-réservation (DELETE en vol : retour, « modifier », minuteur expiré), au
   * plus `PRE_RESERVATION_RELEASE_WAIT_MS` ; tout de suite s'il n'y en a pas. Tant qu'elle n'est pas rendue, le
   * serveur tient la carte cadeau et le code promo pour elle : un résumé du panier parti avant les verrait épuisés et
   * les retirerait à tort.
   */
  whenPreReservationReleased: () => Promise<void>;
  /**
   * Déclaré par l'étape paiement (via `setLocked` du champ code) : création de la pré-réservation, confirmation ou
   * paiement en cours. Le minuteur qui expire alors ne libère pas la pré-réservation (cf. `leavesPreReservation`).
   */
  setCheckoutBusy: (busy: boolean) => void;
  /** « Prendre un autre rendez-vous » : le tunnel repart de zéro (remonté par l'hôte). */
  restartTunnel: () => void;
  /** Base URL where flag SVGs are hosted */
  flagBaseUrl: string;
  /** Rendered on the first step's title line, right-aligned (e.g. /book mode switcher) */
  headerAccessory?: ReactNode;
  /** Lien « Retour au site » de l'en-tête (site vitrine seulement). */
  backToSiteUrl?: string;
  isRedirectFinalizing: boolean;
  redirectNotice?: RedirectNotice;
  /** Bandeau « panier retrouvé » de l'étape prestations (sélection d'une visite précédente). */
  restoredSelection?: RestoredSelectionNotice;
  /** Réponse au bandeau : `true` reprend la sélection (ou l'ajoute), `false` l'oublie. */
  answerRestoredSelection: (resume: boolean) => void;
  /**
   * Fenêtre de 7 jours (premier jour ISO, fenêtres partant d'aujourd'hui) sur laquelle ouvrir la grille
   * de créneaux, issue du lien profond `?d=`. Déjà validée et purgée des dates passées. `undefined` =
   * laisser le tunnel décider.
   */
  initialWeekStart?: string;
  /**
   * Jour `YYYY-MM-DD` demandé par le lien profond `?d=`. Sélectionné dans la grille au premier
   * chargement s'il porte des créneaux ; sinon la grille retombe sur le premier jour disponible.
   */
  initialDate?: string;
}

const BookingCtx = createContext<BookingContextValue | null>(null);

// ── Hook ────────────────────────────────────────

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingCtx);
  if (!ctx) throw new Error('useBooking must be used within BookingProvider');
  return ctx;
}

// ── Provider props ──────────────────────────────

interface BookingProviderProps {
  children: ReactNode;
  callbacks: BookingCallbacks;
  bookingParams?: BookingParams;
  filters?: BookingFilterConfig;
  professionalSlug?: string;
  professionalName?: string;
  prefilledVoucherCode?: string;
  prefilledPromoCode?: string;
  locale?: string;
  currency?: string;
  stripePublishableKey?: string;
  onStepChange?: (step: BookingStep, info?: BookingStepChangeInfo) => void;
  onFunnelEvent?: (event: BookingFunnelEvent) => void;
  labels?: Partial<BookingLabels>;
  redirectToManageOnSuccess?: boolean;
  getManageBookingUrl?: (params: { professionalSlug: string; reservationUuid: string }) => string | undefined;
  successMode?: 'message' | 'manage-inline';
  flagBaseUrl?: string;
  headerAccessory?: ReactNode;
  backToSiteUrl?: string;
  initialRedirectStatus?: string;
  initialBookingUuid?: string;
  handleRedirectReturn?: boolean;
  /** Remonte le tunnel (« Prendre un autre rendez-vous »). */
  onRestart?: () => void;
}

// ── Provider ────────────────────────────────────

export function BookingProvider({
  children,
  callbacks: hostCallbacks,
  bookingParams,
  filters,
  professionalSlug = '',
  professionalName = '',
  prefilledVoucherCode,
  prefilledPromoCode,
  locale = 'fr-FR',
  currency = 'EUR',
  stripePublishableKey,
  onStepChange,
  onFunnelEvent,
  labels: labelOverrides,
  redirectToManageOnSuccess = false,
  getManageBookingUrl,
  successMode = 'message',
  flagBaseUrl = DEFAULT_FLAG_BASE_URL,
  headerAccessory,
  backToSiteUrl,
  initialRedirectStatus,
  initialBookingUuid,
  handleRedirectReturn = true,
  onRestart,
}: BookingProviderProps) {
  const [state, sendToMachine, actorRef] = useMachine(bookingMachine);
  const prevStepRef = useRef<BookingStep | null>(null);

  /* ── Libération de la pré-réservation (DELETE) : un seul appel en vol par uuid, et sa fin connue de tous ──
   * Tous les DELETE passent par ici, ceux du contexte (retour, minuteur) comme ceux de l'étape paiement
   * (`callbacks.onCancelPreReservation` : « modifier », recréation avec d'autres codes) : le résumé du panier
   * et la pose d'un code attendent la fin du dernier (`whenPreReservationReleased`), bornée. */
  const releaseRef = useRef<PreReservationRelease | undefined>(undefined);
  const hostCancelPreReservation = hostCallbacks.onCancelPreReservation;
  const cancelPreReservation = useMemo(
    (): BookingCallbacks['onCancelPreReservation'] =>
      hostCancelPreReservation &&
      ((uuid: string): Promise<void> => {
        const inFlight = releaseRef.current;
        if (inFlight?.uuid === uuid) return inFlight.request;
        const request = hostCancelPreReservation(uuid);
        const release: PreReservationRelease = {
          uuid,
          request,
          settled: new Promise<void>((resolve): void => {
            let finished = false;
            const finish = (): void => {
              if (finished) return;
              finished = true;
              clearTimeout(bound);
              if (releaseRef.current === release) releaseRef.current = undefined;
              resolve();
            };
            // Échec réseau : fini tout de suite ; DELETE qui traîne : fini au délai borné.
            const bound = setTimeout(finish, PRE_RESERVATION_RELEASE_WAIT_MS);
            request.then(finish, finish);
          }),
        };
        releaseRef.current = release;
        return request;
      }),
    [hostCancelPreReservation],
  );
  const callbacks = useMemo(
    (): BookingCallbacks => ({ ...hostCallbacks, onCancelPreReservation: cancelPreReservation }),
    [hostCallbacks, cancelPreReservation],
  );
  const whenPreReservationReleased = useCallback(
    (): Promise<void> => releaseRef.current?.settled ?? Promise.resolve(),
    [],
  );
  const checkoutBusyRef = useRef(false);
  const setCheckoutBusy = useCallback((busy: boolean): void => {
    checkoutBusyRef.current = busy;
  }, []);

  /* Libellés dans la langue du tunnel (repli sur le français), surcharges de l'hôte par-dessus. */
  const labels = useMemo<BookingLabels>(
    (): BookingLabels => ({ ...bookingLabelsFor(locale), ...labelOverrides }),
    [locale, labelOverrides],
  );

  /* Prop params merged with the params returned by onLoadPrestations (backend wins) */
  const params = useMemo<BookingParams>(
    (): BookingParams => ({ ...(bookingParams ?? {}), ...state.context.bookingParams }),
    [bookingParams, state.context.bookingParams],
  );

  const formatPrice = useCallback(
    (cents: number): string =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100),
    [locale, currency],
  );

  const currentStep = stepFromState(state.value as string);

  /* L'expiration de la pré-réservation ne se voit qu'à son événement : `send` la signale à la mesure du
     parcours (branchée plus bas, une fois le retour de paiement connu). */
  const reportExpiredRef = useRef<() => void>(noop);
  /* Quitter la finalisation (retour, lien « modifier » du récap, « Retour au site », minuteur expiré) libère tout de
     suite le créneau et les codes retenus par la pré-réservation, sans attendre son expiration (spec Parcours § 7.6). */
  const releasePreReservation = useCallback((): void => {
    const uuid = actorRef.getSnapshot().context.preReservationUuid;
    if (uuid && cancelPreReservation) cancelPreReservation(uuid).catch(noop);
  }, [actorRef, cancelPreReservation]);
  const send = useCallback(
    (event: BookingEvent): void => {
      if (event.type === 'TIMER_EXPIRED') reportExpiredRef.current();
      if (actorRef.getSnapshot().value === 'checkout' && leavesPreReservation(event, checkoutBusyRef.current)) {
        releasePreReservation();
      }
      sendToMachine(event);
    },
    [sendToMachine, actorRef, releasePreReservation],
  );

  const goBack = useCallback((): void => send({ type: 'BACK' }), [send]);

  /* Sélection d'une visite précédente, proposée par le bandeau de l'étape prestations : rien n'en est repris
     sans réponse du client. */
  const [restorable, setRestorable] = useState<RestorableSelection | undefined>(undefined);
  const multiPrestationAllowed = state.context.bookingParams.allow_multi_prestation !== false;
  const answerRestorable = useCallback(
    (resume: boolean): void => {
      if (!restorable) return;
      if (resume) {
        send({
          type: 'RESTORE_CART',
          items: resumeRestorableSelection(
            actorRef.getSnapshot().context.cartItems,
            restorable,
            multiPrestationAllowed,
          ),
        });
        if (restorable.dropped.length > 0 || restorable.unbookable.length > 0) {
          send({ type: 'PRESELECTIONS_DROPPED', names: restorable.dropped, unbookableNames: restorable.unbookable });
        }
      }
      if (professionalSlug) clearBookingCartRestorable(professionalSlug);
      setRestorable(undefined);
    },
    [restorable, send, actorRef, multiPrestationAllowed, professionalSlug],
  );
  const restoredSelection = useMemo(
    (): RestoredSelectionNotice | undefined =>
      restorable && (multiPrestationAllowed || state.context.cartItems.length === 0)
        ? {
            names: [...new Set(restorable.items.map((item): string => item.prestation.name))],
            currentNames: [...new Set(state.context.cartItems.map((item): string => item.prestation.name))],
            savedAt: restorable.updated_at,
            kind: state.context.cartItems.length > 0 ? 'add' : 'resume',
          }
        : undefined,
    [restorable, multiPrestationAllowed, state.context.cartItems],
  );

  /* Chaque essai relance l'effet de chargement ci-dessous. */
  const [loadAttempt, setLoadAttempt] = useState(0);
  const retryLoad = useCallback((): void => {
    send({ type: 'RETRY' });
    setLoadAttempt((attempt): number => attempt + 1);
  }, [send]);

  /* Stripe redirect return (Klarna, 3DS): shared verification, booking-specific confirmation. */
  const redirectReturn = useRedirectReturn({
    param: REDIRECT_RETURN_PARAM.BOOKING,
    enabled: handleRedirectReturn,
    poll: callbacks.onPollPaymentStatus,
    initialStatus: initialRedirectStatus,
    initialUuid: initialBookingUuid,
    getFinalOutcome: (uuid): RedirectFinalOutcome | undefined => bookingRedirectOutcome(actorRef.getSnapshot(), uuid),
    onSettled: ({ uuid, reservationUuid }): void => {
      if (professionalSlug) clearBookingCart(professionalSlug);
      send({ type: 'PAYMENT_CONFIRMED', reservationUuid: reservationUuid ?? uuid });
    },
    /* Unpaid pre-reservation would hold the slot until it expires (PRE_BOOKING_TIMEOUT_MS, 10 min), blocking an immediate retry
       on the same slot. The backend only cancels a still-unpaid one (and its Stripe intent); on
       failure, expiration remains the safety net. */
    onFailed: (uuid): void => {
      callbacks.onCancelPreReservation?.(uuid).catch(noop);
    },
  });
  const {
    isBlocking: isRedirectFinalizing,
    isBlockingRef: isRedirectFinalizingRef,
    dismiss: dismissRedirect,
  } = redirectReturn;
  const redirectPhase = redirectReturn.phase;

  /* Mesure du parcours (§ 13), muette pendant la vérification d'un retour de paiement : le client ne
     voit alors aucune étape. */
  const funnel = useBookingFunnel({
    currentStep,
    context: state.context,
    onFunnelEvent,
    muted: isRedirectFinalizing,
    currency,
  });
  useEffect((): void => {
    reportExpiredRef.current = funnel.reportExpired;
  }, [funnel.reportExpired]);
  const redirectNotice = useMemo(
    (): RedirectNotice | undefined =>
      redirectPhase
        ? {
            type: redirectPhase,
            message: redirectReturnMessage(redirectPhase, {
              verifying: labels.payment_verifying,
              unverified: labels.payment_redirect_unverified,
              failed: labels.payment_redirect_failed,
              pending: labels.payment_redirect_pending,
              expired: labels.payment_redirect_expired,
            }),
          }
        : undefined,
    [redirectPhase, labels],
  );

  /* A redirect notice describes a past attempt: it must not sit on top of a confirmed booking. */
  useEffect((): void => {
    if (currentStep === 'success') dismissRedirect();
  }, [currentStep, dismissRedirect]);

  /* Notify step changes, avec le rang et le total de l'étape (les mêmes que l'en-tête de progression). */
  useEffect((): void => {
    if (currentStep !== prevStepRef.current && currentStep !== 'loading' && currentStep !== 'error') {
      const previous = prevStepRef.current;
      prevStepRef.current = currentStep;
      const steps = visibleSteps(state.context);
      const index = currentStep === 'success' || currentStep === 'quote' ? -1 : steps.indexOf(currentStep);
      onStepChange?.(currentStep, {
        ...(previous ? { previous_step: previous } : {}),
        ...(index >= 0 ? { step_index: index + 1, step_total: steps.length } : {}),
      });
    }
  }, [currentStep, onStepChange]);

  /* Prestation posée par une fiche : lue une seule fois, gardée pour « Réessayer », et retirée du stockage au premier
     chargement réussi du catalogue seulement — un rechargement après une panne la retrouve (spec Parcours § 2). */
  const incomingRef = useRef<{ read: boolean; line?: BookingCartLine }>({ read: false });

  /* Load data on mount + restore persisted state */
  useEffect((): (() => void) => {
    let cancelled = false;
    const catalogueFilters = {
      prestation_ids: filters?.prestation_ids,
      category_ids: filters?.category_ids,
      place_ids: filters?.place_ids,
      member_ids: filters?.member_ids,
    };
    /* Express : la seule prestation d'abord (une sonde au lieu d'une par prestation), le catalogue ensuite. La
       prestation posée par une fiche est lue ici et passée à `resolveCartEntry`, qui la reprend dans la sélection. */
    if (!incomingRef.current.read) {
      incomingRef.current = {
        read: true,
        line: professionalSlug ? peekBookingCartIncoming(professionalSlug) : undefined,
      };
    }
    const incoming = incomingRef.current.line;
    const expressTarget = expressCatalogueTarget(readBookingCart(professionalSlug), filters, incoming);

    callbacks
      .onLoadPrestations(expressTarget ? { ...catalogueFilters, prestation_ids: [expressTarget] } : catalogueFilters)
      .then((data): void => {
        if (cancelled || actorRef.getSnapshot().status === 'done') return;
        send({
          type: 'DATA_LOADED',
          prestations: data.prestations,
          categories: data.categories,
          pro_places: data.pro_places,
          team_members: data.team_members,
          custom_fields: data.custom_fields,
          booking_params: data.booking_params,
        });

        /* Multi-prestation OFF: carts persisted before the flip and external preselects are truncated to one item */
        const multiPrestationAllowed = data.booking_params?.allow_multi_prestation !== false;

        /* Final states ignore catalogue responses; during verification restore the cart so expiration allows retry. */
        if (actorRef.getSnapshot().status === 'done') return;

        /* Panier unique (fiche du site, barre de panier, visites précédentes) : règle d'entrée dans
           `resolveCartEntry`. Catalogue chargé, la prestation posée par une fiche quitte le stockage : reprise dans
           la sélection, elle ne doit pas revenir au prochain passage. */
        const cart = readBookingCart(professionalSlug);
        const entry = resolveCartEntry({
          cart,
          sameVisit: isBookingCartFromThisVisit(cart),
          incoming,
          preselectIds: filters?.preselect_ids,
          preselectLines: filters?.preselect_lines,
          prestations: data.prestations as BookingPrestation[],
          multiPrestationAllowed,
          forcedMode: filters?.entry_mode,
        });
        if (professionalSlug) takeBookingCartIncoming(professionalSlug);
        if (entry.items.length > 0) send({ type: 'RESTORE_CART', items: entry.items });
        send({ type: 'SET_ENTRY_MODE', mode: entry.entryMode });
        /* Présélections écartées : voir `droppedPreselections` / `unbookablePreselections` (types.ts). */
        if (entry.dropped.length > 0 || entry.unbookable.length > 0) {
          send({ type: 'PRESELECTIONS_DROPPED', names: entry.dropped, unbookableNames: entry.unbookable });
        }
        if (entry.restorable && professionalSlug) {
          setBookingCartRestorable(
            professionalSlug,
            { lines: entry.restorable.lines, updated_at: entry.restorable.updated_at },
            entry.items.map(toBookingCartLine),
          );
          setRestorable(entry.restorable);
        }
        /* Parcours express : une présélection écartée l'INTERDIT (cf. resolveCartEntry), sans quoi le client
           passerait directement au calendrier sans jamais voir que son choix a sauté. */
        if (entry.skipServices) send({ type: 'CONFIRM_SERVICES' });

        if (expressTarget) {
          callbacks
            .onLoadPrestations(catalogueFilters)
            .then((full): void => {
              if (cancelled || actorRef.getSnapshot().status === 'done') return;
              send({ type: 'CATALOGUE_LOADED', prestations: full.prestations, categories: full.categories });
            })
            .catch((): void => {
              /* Le parcours express continue avec sa prestation ; le catalogue reste celui de départ. */
            });
        }
      })
      .catch((err: Error & { status?: unknown }): void => {
        if (cancelled || actorRef.getSnapshot().status === 'done') return;
        // Erreur métier de l'API : son message est écrit pour le client. Une panne réseau (« Failed to
        // fetch ») n'en a pas : l'écran d'erreur affiche alors son texte générique.
        send({ type: 'DATA_ERROR', error: typeof err.status === 'number' ? err.message : undefined });
      });

    return (): void => {
      cancelled = true;
    };
  }, [loadAttempt]);

  /* Résumé du panier : calculé avec les codes posés par `BookingCodesProvider` (cf. BookingCodesContext). */

  /* ── Recalcul live de `is_bookable` (conflit temporel multi-prestation) ──
   * Gaté au strict minimum pour la performance :
   *  - uniquement si `allow_multi_prestation` (sinon le panier ≤ 1 item, rien à recalculer) ;
   *  - uniquement si un lieu effectif est connu (sélectionné, ou unique lieu du pro) — sans lieu,
   *    le backend ne peut pas évaluer un conflit temporel multi-prestation ;
   *  - debounce + AbortController (annule l'appel précédent).
   * Les clés `…Key` (chaînes stables) servent de deps pour éviter qu'un merge de bookability
   * — qui ne change que `is_bookable`, pas les ids — ne re-déclenche l'effet en boucle. */
  const liveBookabilityCartKey = state.context.cartItems
    .map((i): string => {
      const supIds = i.supplements
        .filter((s): boolean => s.selected)
        .map((s): number => s.supplement_id)
        .join('-');
      return `${i.prestation.prestation_id}:${i.selectedPriceIndex}:${i.quantity}:${supIds}`;
    })
    .join('|');
  const liveBookabilityPrestationIdsKey = state.context.prestations.map((p): number => p.prestation_id).join(',');
  const effectivePlaceId =
    state.context.selectedPlaceId ??
    (state.context.availablePlaces.length === 1 ? state.context.availablePlaces[0]?.pro_place_id : undefined);

  useEffect((): (() => void) => {
    const checkBookability = callbacks.onCheckBookability;
    if (!checkBookability || params.allow_multi_prestation === false) return noop;

    const { cartItems, prestations } = state.context;
    // On déclenche dès qu'il y a un panier : lieu connu ⇒ check d'enchaînement à ce lieu ;
    // lieu inconnu (pro multi-lieux, étape « prestations ») ⇒ le backend teste l'enchaînement
    // sur les lieux candidats (1er lieu viable). `pro_place_id` est omis dans ce dernier cas.
    if (cartItems.length === 0) return noop;

    const cartPrestationIds = new Set(cartItems.map((i): number => i.prestation.prestation_id));
    const candidateIds = prestations
      .filter((p): boolean => !cartPrestationIds.has(p.prestation_id))
      .map((p): number => p.prestation_id);
    if (candidateIds.length === 0) return noop;

    const controller = new AbortController();
    const timer = setTimeout((): void => {
      checkBookability({
        slots: cartItems.map(
          (item): { prestation_id: number; duration: number; quantity: number; selected_supplement_ids: number[] } => {
            const selectedSupplements = item.supplements.filter((s): boolean => s.selected);
            const supplementDuration = selectedSupplements.reduce(
              (sum, s): number => sum + (s.duration_minutes ?? 0),
              0,
            );
            return {
              prestation_id: item.prestation.prestation_id,
              duration: item.selectedPrice.duration_minutes + supplementDuration,
              quantity: item.quantity,
              selected_supplement_ids: selectedSupplements.map((s): number => s.supplement_id),
            };
          },
        ),
        candidate_prestation_ids: candidateIds,
        ...(effectivePlaceId !== undefined ? { pro_place_id: effectivePlaceId } : {}),
        signal: controller.signal,
      })
        .then((bookability): void => {
          send({ type: 'BOOKABILITY_REFRESHED', bookability });
        })
        .catch((): void => {
          /* appel annulé (debounce) ou erreur réseau — on garde le is_bookable courant */
        });
    }, 300);

    return (): void => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    liveBookabilityCartKey,
    liveBookabilityPrestationIdsKey,
    effectivePlaceId,
    params.allow_multi_prestation,
    callbacks,
    send,
  ]);

  /* Sélection enregistrée dans le panier unique à chaque changement, vide comprise (la sélection mise de côté
     pour le bandeau y reste). Rien n'est écrit avant que le catalogue ait relu le panier, ni sur l'écran
     d'erreur : une panne de chargement ne doit pas effacer la sélection du client. */
  useEffect((): void => {
    if (
      currentStep === 'success' ||
      currentStep === 'loading' ||
      currentStep === 'error' ||
      !professionalSlug ||
      state.context.dataLoading ||
      isRedirectFinalizingRef.current
    )
      return;
    saveBookingCartLines(professionalSlug, state.context.cartItems.map(toBookingCartLine));
  }, [professionalSlug, isRedirectFinalizing, currentStep, state.context.dataLoading, state.context.cartItems]);

  /* Réservation confirmée : le panier est vidé (la sélection mise de côté avec), le brouillon des coordonnées aussi. */
  useEffect((): void => {
    if (currentStep === 'success' && professionalSlug) {
      clearBookingCart(professionalSlug);
      clearCheckoutDraft(professionalSlug);
    }
  }, [currentStep, professionalSlug]);

  const restartTunnel = useCallback((): void => (onRestart ?? noop)(), [onRestart]);

  /* Fenêtre visée par le lien profond : résolue une fois ici (validation + rejet du passé) plutôt
   * qu'à chaque rendu de l'étape créneaux. */
  const initialWeekStart = useMemo(
    (): string | undefined => resolveInitialWindowStart(filters?.initial_date),
    [filters?.initial_date],
  );

  /* Même validation pour le jour : une chaîne malformée ne doit pas circuler jusqu'à la grille. */
  const initialDate = useMemo(
    (): string | undefined =>
      filters?.initial_date && isValidISODate(filters.initial_date) ? filters.initial_date : undefined,
    [filters?.initial_date],
  );

  const value = useMemo<BookingContextValue>(
    (): BookingContextValue => ({
      ctx: state.context,
      currentStep,
      send,
      callbacks,
      labels,
      params,
      locale,
      currency,
      stripePublishableKey,
      professionalName,
      professionalSlug,
      prefilledVoucherCode,
      prefilledPromoCode,
      redirectToManageOnSuccess,
      getManageBookingUrl,
      successMode,
      formatPrice,
      goBack,
      retryLoad,
      releasePreReservation,
      whenPreReservationReleased,
      setCheckoutBusy,
      restartTunnel,
      flagBaseUrl,
      headerAccessory,
      backToSiteUrl,
      initialWeekStart,
      initialDate,
      isRedirectFinalizing,
      redirectNotice,
      restoredSelection,
      answerRestoredSelection: answerRestorable,
    }),
    [
      state.context,
      currentStep,
      send,
      callbacks,
      labels,
      params,
      locale,
      currency,
      stripePublishableKey,
      professionalName,
      professionalSlug,
      prefilledVoucherCode,
      prefilledPromoCode,
      redirectToManageOnSuccess,
      getManageBookingUrl,
      successMode,
      formatPrice,
      goBack,
      retryLoad,
      releasePreReservation,
      whenPreReservationReleased,
      setCheckoutBusy,
      restartTunnel,
      flagBaseUrl,
      headerAccessory,
      backToSiteUrl,
      initialWeekStart,
      initialDate,
      isRedirectFinalizing,
      redirectNotice,
      restoredSelection,
      answerRestorable,
    ],
  );

  return <BookingCtx.Provider value={value}>{children}</BookingCtx.Provider>;
}
